/**
 * Адреса витрины раздела /shared (ingdg_com#192).
 *
 *   /shared                 витрина: полки «Для вас», «Новое», рубрики
 *   /shared/tag/<slug>      рубрика, собранная из тега
 *   ...?page=N              страница списка
 *
 * Адрес файла (`/shared/<папки>/<имя>--<uuid>`) сюда не относится: у него
 * в последнем сегменте uuid, у витрины его нет никогда. Поэтому папка
 * автора с именем «tag» не спутается с рубрикой: её файлы несут uuid.
 *
 * Модуль без Next: его читают middleware, страница и тесты.
 */

import { SHARED_PREFIX, sharedIdFromPath, sharedSlug } from '@/lib/shared-url';

/** Сегмент рубрики в адресе. */
export const SHARED_TAG_SEGMENT = 'tag';

/** Что за страница витрины по пути без локали. `null` — не витрина. */
export type CatalogRoute = { kind: 'root' } | { kind: 'tag'; slug: string };

export function catalogRoute(bare: string): CatalogRoute | null {
  const clean = (bare.split('?')[0] ?? '').replace(/\/+$/, '');
  if (clean === SHARED_PREFIX) return { kind: 'root' };
  if (sharedIdFromPath(clean) !== null) return null;

  const parts = clean.split('/').filter((s) => s !== '');
  if (parts.length === 3 && parts[0] === 'shared' && parts[1] === SHARED_TAG_SEGMENT) {
    const slug = parts[2] ?? '';
    // Слаг рубрики всегда в каноническом виде: другой вид означает
    // выдуманный адрес, и отвечать на него страницей незачем.
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? { kind: 'tag', slug } : null;
  }
  return null;
}

/** Адрес рубрики по имени тега. */
export function rubricPath(tag: string): string {
  return `${SHARED_PREFIX}/${SHARED_TAG_SEGMENT}/${sharedSlug(tag)}`;
}

/**
 * Номер страницы списка из строки запроса. Всё, что не целое число от
 * двух, это первая страница: `?page=1`, `?page=abc` и `?page=-3` не
 * должны становиться отдельными адресами одного и того же списка.
 */
export function catalogPage(search: string | URLSearchParams | undefined): number {
  const params =
    typeof search === 'string' || search === undefined ? new URLSearchParams(search ?? '') : search;
  const raw = params.get('page') ?? '';
  if (!/^\d+$/.test(raw)) return 1;
  const n = Number(raw);
  return n >= 2 ? n : 1;
}

/** Предел длины поискового запроса: как у API. */
export const CATALOG_QUERY_MAX = 200;

/**
 * Поисковый запрос витрины из строки запроса (`?q=`). Пробелы
 * схлопнуты, длина ограничена. Пусто — поиска нет, это обычная витрина.
 */
export function catalogQuery(search: string | URLSearchParams | undefined): string {
  const params =
    typeof search === 'string' || search === undefined ? new URLSearchParams(search ?? '') : search;
  const q = (params.get('q') ?? '').trim().split(/\s+/).join(' ');
  // По символам, а не по единицам UTF-16: обрезка посреди эмодзи
  // оставила бы в запросе битую половинку.
  return Array.from(q).slice(0, CATALOG_QUERY_MAX).join('');
}

/** Адрес страницы списка: первая без параметра, остальные с `?page=N`. */
export function catalogPageHref(base: string, page: number): string {
  return page <= 1 ? base : `${base}?page=${String(page)}`;
}

/**
 * Последние открытые файлы для «Для вас»: свежий первым, без повторов,
 * не больше `limit`. Хранятся в браузере, на сервер уходят только
 * идентификаторы в запросе рекомендаций.
 */
export function rememberSeen(prev: readonly string[], id: string, limit = 20): string[] {
  return [id, ...prev.filter((x) => x !== id)].slice(0, limit);
}

/** Ключ localStorage с историей просмотров витрины. */
export const SEEN_KEY = 'ais-shared-seen-v1';
